import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, Box, IconButton, Tooltip, Typography } from "@mui/material";

import { AREA_COLOR_MAP } from "@/shared/colors";
import {
	ALL_PRODUCT_AREAS,
	getProductArea,
	PRODUCT_AREA_ASSET_ID,
	PRODUCT_AREA_LABEL,
	Product,
} from "@/shared/models/Product";

export type LineageEditProps = {
	canAddParent: boolean;
	onAddParentClick: () => void;
	onRemoveParent: () => void;
	onAddChildClick: () => void;
	onRemoveChild: (childId: number) => void;
	isDisabled?: boolean;
};

type Props = {
	product: Product;
	onNavigate: (productId: number) => void;
	/** When true, chips don't navigate (used inside EditSpecsModal). */
	disableNavigate?: boolean;
	edit?: LineageEditProps;
};

const AREA_LABEL_BY_ASSET_ID: Record<number, string> = Object.fromEntries(
	ALL_PRODUCT_AREAS.map((area) => [
		PRODUCT_AREA_ASSET_ID[area],
		PRODUCT_AREA_LABEL[area],
	]),
);

const NEUTRAL_COLOR = "#9e9e9e";

const areaColorOf = (assetId: number | null): string =>
	(assetId != null && AREA_COLOR_MAP[assetId]) || NEUTRAL_COLOR;

const areaLabelOf = (assetId: number | null): string =>
	(assetId != null && AREA_LABEL_BY_ASSET_ID[assetId]) || "—";

type ChipVariant = "ancestor" | "current" | "child";

type ChipProps = {
	variant: ChipVariant;
	areaAssetId: number | null;
	partNumber: string;
	extendedPartNumber?: string | null;
	onClick?: () => void;
	onRemove?: () => void;
	removeDisabled?: boolean;
};

const LineageChip = ({
	variant,
	areaAssetId,
	partNumber,
	extendedPartNumber,
	onClick,
	onRemove,
	removeDisabled,
}: ChipProps) => {
	const color = areaColorOf(areaAssetId);
	const label = areaLabelOf(areaAssetId);

	const isAncestor = variant === "ancestor";
	const isCurrent = variant === "current";
	const isChild = variant === "child";

	const chipSx = {
		position: "relative" as const,
		display: "inline-flex",
		flexDirection: "column" as const,
		alignItems: "center",
		justifyContent: "center",
		lineHeight: 1.2,
		px: 1.75,
		py: 0.9,
		borderRadius: 1,
		minWidth: 120,
		userSelect: "none" as const,
		transition: "all 120ms ease",
		cursor: onClick ? ("pointer" as const) : ("default" as const),
		...(isAncestor && {
			backgroundColor: "transparent",
			color: color,
			border: `1.5px solid ${color}`,
			...(onClick && {
				"&:hover": {
					backgroundColor: alpha(color, 0.08),
				},
			}),
		}),
		...(isCurrent && {
			backgroundColor: color,
			color: "#fff",
			border: "2px solid #fff",
			outline: `2px solid ${color}`,
			boxShadow: `0 0 0 3px ${alpha(color, 0.25)}`,
			fontWeight: 800,
		}),
		...(isChild && {
			backgroundColor: alpha(color, 0.12),
			color: "text.primary",
			border: `1px dashed ${alpha(color, 0.6)}`,
			opacity: onClick ? 0.7 : 0.85,
			...(onClick && {
				"&:hover": {
					opacity: 1,
					backgroundColor: alpha(color, 0.2),
				},
			}),
		}),
	};

	const chip = (
		<Box sx={chipSx} onClick={onClick}>
			<Typography
				component="span"
				sx={{
					fontSize: "0.68rem",
					letterSpacing: "0.06em",
					textTransform: "uppercase",
					opacity: isChild ? 0.85 : 0.9,
					fontWeight: 600,
				}}
			>
				{label}
			</Typography>
			<Typography
				component="span"
				sx={{
					fontSize: "0.92rem",
					fontWeight: isCurrent ? 800 : 700,
					lineHeight: 1.2,
					maxWidth: 200,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{partNumber}
			</Typography>
			{extendedPartNumber && (
				<Typography
					component="span"
					sx={{
						fontSize: "0.68rem",
						opacity: 0.85,
						lineHeight: 1.2,
						maxWidth: 200,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{extendedPartNumber}
				</Typography>
			)}
			{onRemove && (
				<IconButton
					size="small"
					disabled={removeDisabled}
					onClick={(e) => {
						e.stopPropagation();
						onRemove();
					}}
					sx={{
						position: "absolute",
						top: -8,
						right: -8,
						width: 18,
						height: 18,
						p: 0,
						bgcolor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						color: "text.secondary",
						"&:hover": {
							bgcolor: "error.main",
							color: "#fff",
							borderColor: "error.main",
						},
					}}
				>
					<CloseIcon sx={{ fontSize: "0.85rem" }} />
				</IconButton>
			)}
		</Box>
	);

	const tooltip = isCurrent
		? `Current: ${label} · ${partNumber}`
		: onClick
			? isChild
				? `Go to child: ${label} · ${partNumber}`
				: `Go to ancestor: ${label} · ${partNumber}`
			: `${isChild ? "Child" : "Ancestor"}: ${label} · ${partNumber}`;

	return (
		<Tooltip title={tooltip} arrow placement="top" disableInteractive>
			{chip}
		</Tooltip>
	);
};

const ChainArrow = () => (
	<ChevronRightIcon
		sx={{ color: "text.disabled", mx: -0.25, fontSize: "1.35rem" }}
	/>
);

const FutureDivider = () => (
	<Box
		sx={{
			alignSelf: "stretch",
			mx: 1,
			width: "3px",
			backgroundImage:
				"repeating-linear-gradient(to bottom, rgba(0,0,0,0.25) 0 3px, transparent 3px 6px)",
		}}
	/>
);

const AddSlotChip = ({
	label,
	onClick,
	disabled,
}: {
	label: string;
	onClick: () => void;
	disabled?: boolean;
}) => {
	return (
		<Box
			onClick={() => {
				if (!disabled) onClick();
			}}
			sx={{
				display: "inline-flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				lineHeight: 1.2,
				px: 1.25,
				py: 0.6,
				minWidth: 120,
				borderRadius: 1,
				border: "1.5px dashed",
				borderColor: "text.disabled",
				color: "text.secondary",
				cursor: disabled ? "default" : "pointer",
				opacity: disabled ? 0.4 : 0.75,
				transition: "all 120ms ease",
				"&:hover": disabled
					? {}
					: {
							opacity: 1,
							borderColor: "primary.main",
							color: "primary.main",
							backgroundColor: "action.hover",
						},
			}}
		>
			<AddIcon sx={{ fontSize: "1.1rem" }} />
			<Typography
				component="span"
				sx={{
					fontSize: "0.68rem",
					letterSpacing: "0.06em",
					textTransform: "uppercase",
					fontWeight: 700,
				}}
			>
				{label}
			</Typography>
		</Box>
	);
};

export const ProductLineageBar = ({
	product,
	onNavigate,
	disableNavigate,
	edit,
}: Props) => {
	const ancestors = product.Ancestors ?? [];
	const children = product.Children ?? [];

	const currentArea = getProductArea(product);
	const currentAreaAssetId =
		currentArea != null ? PRODUCT_AREA_ASSET_ID[currentArea] : null;

	const hasNoParent = ancestors.length === 0;
	const showAddParentSlot = edit != null && hasNoParent && edit.canAddParent;
	const directParentProductId =
		ancestors.length > 0 ? ancestors[ancestors.length - 1].ProductId : null;

	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				columnGap: 0.75,
				rowGap: 1.5,
				width: "100%",
			}}
		>
			{showAddParentSlot && edit && (
				<Box sx={{ display: "inline-flex", alignItems: "center" }}>
					<AddSlotChip
						label="Add Parent"
						onClick={edit.onAddParentClick}
						disabled={edit.isDisabled}
					/>
					<ChainArrow />
				</Box>
			)}

			{/* Ancestors (root-first). Only the direct parent gets × in edit mode. */}
			{ancestors.map((a) => {
				const isDirectParent = a.ProductId === directParentProductId;
				return (
					<Box
						key={a.ProductId}
						sx={{ display: "inline-flex", alignItems: "center" }}
					>
						<LineageChip
							variant="ancestor"
							areaAssetId={a.AreaAssetId}
							partNumber={a.PartNumber}
							extendedPartNumber={a.ExtendedPartNumber}
							onClick={
								disableNavigate ? undefined : () => onNavigate(a.ProductId)
							}
							onRemove={
								isDirectParent && edit?.onRemoveParent
									? edit.onRemoveParent
									: undefined
							}
							removeDisabled={edit?.isDisabled}
						/>
						<ChainArrow />
					</Box>
				);
			})}

			<LineageChip
				variant="current"
				areaAssetId={currentAreaAssetId}
				partNumber={product.PartNumber}
				extendedPartNumber={product.ExtendedPartNumber}
			/>

			{children.length === 1 && <ChainArrow />}
			{children.length > 1 && <FutureDivider />}
			{children.map((c) => (
				<LineageChip
					key={c.ProductId}
					variant="child"
					areaAssetId={c.AreaAssetId}
					partNumber={c.PartNumber}
					extendedPartNumber={c.ExtendedPartNumber}
					onClick={disableNavigate ? undefined : () => onNavigate(c.ProductId)}
					onRemove={
						edit?.onRemoveChild
							? () => edit.onRemoveChild(c.ProductId)
							: undefined
					}
					removeDisabled={edit?.isDisabled}
				/>
			))}

			{edit != null && (
				<AddSlotChip
					label="Add Child"
					onClick={edit.onAddChildClick}
					disabled={edit.isDisabled}
				/>
			)}
		</Box>
	);
};
